/**
 * Copy / download for code blocks in an answer.
 *
 * Feedback, recurring and again 2026-10-27: an answer containing a
 * PowerShell script could only be copied as the WHOLE reply, so the user
 * had to cut the script out by hand and save it themselves. Each fenced
 * block now carries its own bar: the language, "Kopiér" and "Download".
 *
 * The bar is added AFTER DOMPurify has sanitised the answer, and contains
 * nothing from the model except the language name (escaped) — the code
 * itself is read from the rendered <code> element at click time.
 */

/** Language (as written after ```) → file extension and default name. */
const LANGS: Record<string, { ext: string; name: string }> = {
  powershell: { ext: "ps1", name: "script" },
  ps1: { ext: "ps1", name: "script" },
  pwsh: { ext: "ps1", name: "script" },
  bash: { ext: "sh", name: "script" },
  sh: { ext: "sh", name: "script" },
  shell: { ext: "sh", name: "script" },
  zsh: { ext: "sh", name: "script" },
  bat: { ext: "bat", name: "script" },
  batch: { ext: "bat", name: "script" },
  cmd: { ext: "cmd", name: "script" },
  python: { ext: "py", name: "script" },
  py: { ext: "py", name: "script" },
  sql: { ext: "sql", name: "query" },
  kql: { ext: "kql", name: "query" },
  kusto: { ext: "kql", name: "query" },
  json: { ext: "json", name: "data" },
  yaml: { ext: "yaml", name: "config" },
  yml: { ext: "yaml", name: "config" },
  xml: { ext: "xml", name: "data" },
  csv: { ext: "csv", name: "data" },
  ini: { ext: "ini", name: "config" },
  toml: { ext: "toml", name: "config" },
  reg: { ext: "reg", name: "settings" },
  javascript: { ext: "js", name: "script" },
  js: { ext: "js", name: "script" },
  typescript: { ext: "ts", name: "script" },
  ts: { ext: "ts", name: "script" },
  html: { ext: "html", name: "page" },
  css: { ext: "css", name: "styles" },
  csharp: { ext: "cs", name: "Program" },
  cs: { ext: "cs", name: "Program" },
  vb: { ext: "vbs", name: "script" },
  vbscript: { ext: "vbs", name: "script" },
  dockerfile: { ext: "Dockerfile", name: "" },
  text: { ext: "txt", name: "tekst" },
  txt: { ext: "txt", name: "tekst" },
};

const KNOWN_EXT = new Set(Object.values(LANGS).map((l) => l.ext.toLowerCase()).concat(["psm1", "psd1"]));
/** A filename the code names in its first lines, e.g. `# Install-Printer.ps1`. */
const NAME_IN_CODE = /([A-Za-z0-9_][A-Za-z0-9_.-]{0,80}\.([A-Za-z0-9]{1,10}))\b/;

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function languageOf(code: Element): string {
  const cls = Array.from(code.classList).find((c) => c.startsWith("language-"));
  return cls ? cls.slice("language-".length).toLowerCase() : "";
}

/** The filename a download gets: one the code names itself (with a known
 *  extension that fits the language), else a default per language. */
export function fileNameFor(lang: string, code: string): string {
  const meta = LANGS[lang] ?? { ext: "txt", name: "kode" };
  if (meta.ext === "Dockerfile") return "Dockerfile";
  for (const line of code.split("\n").slice(0, 3)) {
    const m = NAME_IN_CODE.exec(line);
    if (!m) continue;
    const ext = m[2].toLowerCase();
    // Only a name whose extension fits the block: "# see Readme.md" at
    // the top of a PowerShell script must not name the download Readme.md.
    const fits =
      ext === meta.ext ||
      meta.ext === "txt" ||
      (meta.ext === "ps1" && (ext === "psm1" || ext === "psd1"));
    if (fits && KNOWN_EXT.has(ext)) return m[1];
  }
  return `${meta.name}.${meta.ext}`;
}

/** The bytes a file should have to RUN on the machine it is meant for.
 *  - .ps1/.psm1: UTF-8 WITH a BOM, CRLF — Windows PowerShell 5.1 reads a
 *    BOM-less file as ANSI and garbles æøå in strings.
 *  - .bat/.cmd: CRLF and NO BOM — a BOM breaks the first command.
 *  - .reg: UTF-16 LE with a BOM, CRLF — what regedit expects for
 *    "Windows Registry Editor Version 5.00".
 *  - everything else: UTF-8 as written. */
export function fileBytes(fileName: string, code: string): { parts: BlobPart[]; type: string } {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  const crlf = code.replace(/\r?\n/g, "\r\n");
  if (ext === "ps1" || ext === "psm1" || ext === "psd1") {
    return { parts: ["\uFEFF", crlf], type: "text/plain;charset=utf-8" };
  }
  if (ext === "bat" || ext === "cmd") {
    return { parts: [crlf], type: "text/plain;charset=utf-8" };
  }
  if (ext === "reg") {
    const text = "\uFEFF" + crlf;
    const buf = new Uint8Array(text.length * 2);
    for (let i = 0; i < text.length; i++) {
      const c = text.charCodeAt(i);
      buf[i * 2] = c & 0xff;
      buf[i * 2 + 1] = c >> 8;
    }
    return { parts: [buf], type: "text/plain;charset=utf-16le" };
  }
  return { parts: [code], type: "text/plain;charset=utf-8" };
}

/** Wrap every <pre><code> in sanitised answer HTML with a bar. Safe to run
 *  on partial (streaming) answers. */
export function addCodeBars(html: string): string {
  if (!html.includes("<pre")) return html;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  tpl.content.querySelectorAll("pre > code").forEach((code) => {
    const pre = code.parentElement as HTMLElement;
    if (pre.parentElement?.classList.contains("codeblock")) return;
    const lang = languageOf(code);
    const ext = (LANGS[lang] ?? { ext: "txt" }).ext;
    const wrap = document.createElement("div");
    wrap.className = "codeblock";
    const bar = document.createElement("div");
    bar.className = "codeblock__bar";
    bar.innerHTML =
      `<span class="codeblock__lang">${escapeHtml(lang || "kode")}</span>` +
      `<span class="codeblock__actions">` +
      `<button type="button" class="codeblock__btn" data-code-act="copy">Kopiér</button>` +
      `<button type="button" class="codeblock__btn" data-code-act="download">Download .${escapeHtml(ext)}</button>` +
      `</span>`;
    pre.replaceWith(wrap);
    wrap.append(bar, pre);
  });
  return tpl.innerHTML;
}

/** Handle a click inside an answer. Returns true when it was a code-bar click. */
export async function handleCodeBarClick(
  event: MouseEvent,
  notify: { ok: (msg: string) => void; fail: (msg: string) => void },
): Promise<boolean> {
  const btn = (event.target as HTMLElement | null)?.closest(
    "button[data-code-act]",
  ) as HTMLElement | null;
  if (!btn) return false;
  event.preventDefault();
  const code = btn.closest(".codeblock")?.querySelector("pre > code");
  if (!code) return true;
  const text = code.textContent ?? "";
  if (btn.dataset.codeAct === "copy") {
    try {
      await navigator.clipboard.writeText(text);
      notify.ok("Koden er kopieret");
    } catch {
      notify.fail("Kunne ikke kopiere — browseren tillod det ikke");
    }
    return true;
  }
  const name = fileNameFor(languageOf(code), text);
  const { parts, type } = fileBytes(name, text);
  const url = URL.createObjectURL(new Blob(parts, { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}
